import { useMemo } from 'react';
import type { FC } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Line, Bar } from 'react-chartjs-2';
import { StatCard, ChartCard } from '../components/Cards';
import { groupMetrics } from '../utils/group-metrics';
import { baseChartOptions, lineDataset, barDataset, CHART_COLORS } from '../utils/charts';
import type { OpsContext } from '../types';

const Signups: FC = () => {
  const { current, history, grouping } = useOutletContext<OpsContext>();
  const buckets = useMemo(() => groupMetrics(history, grouping), [history, grouping]);
  const labels = buckets.map((b) => b.label);

  const signups = current?.totalUsers ?? 0;
  const onboarded = current?.usersOnboarded ?? 0;
  const firstArticle = current?.usersWithFirstArticle ?? 0;
  const firstDeposit = current?.usersWithFirstDeposit ?? 0;
  const pct = (num: number, denom: number) =>
    denom > 0 ? `${Math.round((num / denom) * 100)}%` : '—';

  const cumulativeData = {
    labels,
    datasets: [
      lineDataset('Total users', buckets.map((b) => b.totalUsers), CHART_COLORS.signups),
      lineDataset('Onboarded', buckets.map((b) => b.usersOnboarded), CHART_COLORS.onboarded),
      lineDataset('First article', buckets.map((b) => b.usersWithFirstArticle), CHART_COLORS.firstArticle),
      lineDataset('First deposit', buckets.map((b) => b.usersWithFirstDeposit), CHART_COLORS.firstDeposit),
    ],
  };

  const perPeriodData = {
    labels,
    datasets: [
      barDataset('New signups', buckets.map((b) => b.newSignups), CHART_COLORS.signups),
      barDataset('Onboarded', buckets.map((b) => b.dailyOnboarded), CHART_COLORS.onboarded),
      barDataset('First article', buckets.map((b) => b.dailyFirstArticle), CHART_COLORS.firstArticle),
      barDataset('First deposit', buckets.map((b) => b.dailyFirstDeposit), CHART_COLORS.firstDeposit),
    ],
  };

  return (
    <>
      <div className="row row-cols-2 row-cols-md-4 g-2 mb-3">
        <StatCard label="Total users" value={signups.toString()} variant="info" />
        <StatCard label="Onboarded" value={onboarded.toString()} variant="primary" />
        <StatCard label="First article" value={firstArticle.toString()} variant="success" />
        <StatCard label="First deposit" value={firstDeposit.toString()} variant="warning" />
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title text-muted mb-3">Funnel (latest snapshot)</h6>
          <div className="row row-cols-2 row-cols-md-4 g-2">
            <FunnelStage label="Signed up" count={signups} share="100%" variant="info" />
            <FunnelStage label="Onboarded" count={onboarded} share={pct(onboarded, signups)} variant="primary" />
            <FunnelStage label="First article" count={firstArticle} share={pct(firstArticle, onboarded)} variant="success" />
            <FunnelStage label="First deposit" count={firstDeposit} share={pct(firstDeposit, firstArticle)} variant="warning" />
          </div>
        </div>
      </div>

      {buckets.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <ChartCard title="Cumulative">
            <Line options={baseChartOptions} data={cumulativeData} />
          </ChartCard>
          <ChartCard title="Per period">
            <Bar options={baseChartOptions} data={perPeriodData} />
          </ChartCard>
        </>
      )}
    </>
  );
};

const FunnelStage: FC<{ label: string; count: number; share: string; variant: string }> = ({
  label,
  count,
  share,
  variant,
}) => (
  <div className="col">
    <div className={`card border-${variant} h-100`}>
      <div className="card-body text-center py-3">
        <div className="text-muted small">{label}</div>
        <div className="fs-4 fw-bold">{count}</div>
        <div className={`small text-${variant}`}>{share}</div>
      </div>
    </div>
  </div>
);

const EmptyState: FC = () => (
  <div className="text-center text-muted py-5">
    <p>No metrics data in the selected range. Metrics are computed daily at 03:00 UTC.</p>
  </div>
);

export default Signups;
